import { useMemo, useState } from "react";
import { PaperGradientBG } from "../kits";
import { FaCode, FaChalkboardTeacher, FaComments } from "react-icons/fa";
import TerminalDetailOverlay from "./TerminalDetailOverlay";

const DEFAULT_PILLARS = [
  {
    id: "entwicklung",
    icon: FaCode,
    cmd: "build --stack",
    title: "Entwicklung",
    desc: "React · Angular · Flutter · System Architecture",
    hint: "von idee zur loesung.",
  },
  {
    id: "training",
    icon: FaChalkboardTeacher,
    cmd: "teach --workshop",
    title: "Training",
    desc: "Dozent · Kurse · Team-Enablement",
    hint: "wissen, das im alltag wirkt.",
  },
  {
    id: "beratung",
    icon: FaComments,
    cmd: "consult --review",
    title: "beratung",
    title2: "Architektur-Reviews · Code-Audits · Strategie",
    desc: "Architektur-Reviews · Code-Audits · Strategie",
    hint: "klarheit fuer entscheidungen.",
  },
];

const DEVELOPMENT_DETAIL_LINES = [
  { type: "prompt", text: "cd /services/entwicklung" },
  { type: "status", text: "oeffne detailansicht ..." },
  { type: "prompt", text: "cat scope.md" },
    { type: "prompt", text: "" },
  { type: "response", text: "ich entwickle web-apps, interne tools und produkt-nahe plattformen." },
  { type: "response", text: "fokus: klare architektur, wartbarkeit und messbarer business-impact." },
   { type: "prompt", text: "" },
  { type: "prompt", text: "cat stack.md" },
  { type: "response", text: "frontend: react, angular, tailwind, framer motion." },
  { type: "response", text: "backend: node, APIs, integrations, auth und datenfluesse." },
  { type: "response", text: "mobile/desktop: flutter und hybride loesungen bei bedarf." },
   { type: "prompt", text: "" },
  { type: "prompt", text: "cat process.md" },
  { type: "response", text: "1) analyse: ziel, constraints, risiko." },
  { type: "response", text: "2) architektur: aufbau, module, schnittstellen." },
  { type: "response", text: "3) umsetzung: iterativ mit schnellen review-schleifen." },
  { type: "response", text: "4) handover: dokumentation, enablement, naechste schritte." },
   { type: "prompt", text: "" },
  { type: "prompt", text: "cat output.md" },
  { type: "response", text: "loesungen, die team und anwender wirklich nutzen." },
  { type: "status", text: "bereit fuer ein == saghallo" },
];

const TRAINING_DETAIL_LINES = [
  { type: "prompt", text: "cd /services/training" },
  { type: "status", text: "oeffne detailansicht ..." },
   { type: "prompt", text: "" },
  { type: "prompt", text: "cat scope.md" },
  { type: "response", text: "ich begleite einzelpersonen, teams und unternehmen beim aufbau echter umsetzungsfaehigkeit." },
  { type: "response", text: "fokus: praxis, transfer in den arbeitsalltag und nachhaltiges verstaendnis." },
   { type: "prompt", text: "" },
  { type: "prompt", text: "cat formats.md" },
  { type: "response", text: "1:1 sessions, team-workshops, inhouse-trainings und modulare kursreihen." },
  { type: "response", text: "themen: web-dev, architektur, workflow, tooling und content-systeme." },
   { type: "prompt", text: "" },
  { type: "prompt", text: "cat method.md" },
  { type: "response", text: "weniger folien, mehr echte beispiele und konkrete aufgaben." },
  { type: "response", text: "wir arbeiten an deinen realen cases statt an abstrakten demo-projekten." },
  { type: "prompt", text: "cat output.md" },
  { type: "response", text: "dein team wird schneller, sicherer und trifft bessere technische entscheidungen." },
  { type: "status", text: "naechster schritt == saghallo" },
];

const CONSULTING_DETAIL_LINES = [
  { type: "prompt", text: "cd /services/beratung" },
  { type: "status", text: "oeffne detailansicht ..." },
  { type: "prompt", text: "cat scope.md" },
  { type: "response", text: "ich analysiere architektur, codebasis und team-setup fuer belastbare entscheidungen." },
  { type: "response", text: "ziel: klarheit bei komplexitaet, ohne over-engineering." },
  { type: "prompt", text: "cat modules.md" },
  { type: "response", text: "architektur-reviews, code-audits, roadmap-checks und technische priorisierung." },
  { type: "response", text: "optional: sparring fuer lead-devs, cto oder produktverantwortliche." },
  { type: "prompt", text: "cat process.md" },
  { type: "response", text: "1) diagnose  2) handlungsempfehlung  3) umsetzungsbegleitung." },
  { type: "response", text: "alles dokumentiert, nachvollziehbar und direkt anschlussfaehig." },
  { type: "prompt", text: "cat output.md" },
  { type: "response", text: "du bekommst entscheidungssicherheit, weniger reibung und einen klaren technischen kurs." },
  { type: "status", text: "bereit fuer review == saghallo" },
];

export const SERVICE_DETAIL_CONFIG = {
  entwicklung: {
    subtitle: "ls services/entwicklung/",
    lines: DEVELOPMENT_DETAIL_LINES,
    storageKey: "serviceDetail:entwicklung:typed:v1",
    ctaLabel: "saghallo",
    ctaMode: "inlineContact",
  },
  training: {
    subtitle: "ls services/training/",
    lines: TRAINING_DETAIL_LINES,
    storageKey: "serviceDetail:training:typed:v1",
    ctaLabel: "saghallo",
    ctaMode: "inlineContact",
  },
  beratung: {
    subtitle: "ls services/beratung/",
    lines: CONSULTING_DETAIL_LINES,
    storageKey: "serviceDetail:beratung:typed:v1",
    ctaLabel: "saghallo",
    ctaMode: "inlineContact",
  },
};

export default function ServiceTerminal({ className = "", pillars }) {
  const [activeDetailId, setActiveDetailId] = useState(null);
  const items = (pillars && pillars.length ? pillars : DEFAULT_PILLARS);
  const orderedDetailIds = items
    .map((pillar) => pillar.id)
    .filter((id, index, arr) => arr.indexOf(id) === index && Boolean(SERVICE_DETAIL_CONFIG[id]));
  const activeDetailIndex = activeDetailId ? orderedDetailIds.indexOf(activeDetailId) : -1;
  const activePillar = useMemo(
    () => items.find((pillar) => pillar.id === activeDetailId) || null,
    [activeDetailId, items]
  );
  const activeDetail = activeDetailId ? SERVICE_DETAIL_CONFIG[activeDetailId] : null;

  const canOpenDetail = (pillarId) => Boolean(SERVICE_DETAIL_CONFIG[pillarId]);
  const openDetail = (pillarId) => {
    if (!canOpenDetail(pillarId)) return;
    setActiveDetailId(pillarId);
  };
  const closeDetail = () => setActiveDetailId(null);
  const handlePrev = () => {
    if (activeDetailIndex <= 0) return;
    setActiveDetailId(orderedDetailIds[activeDetailIndex - 1]);
  };
  const handleNext = () => {
    if (activeDetailIndex < 0 || activeDetailIndex >= orderedDetailIds.length - 1) return;
    setActiveDetailId(orderedDetailIds[activeDetailIndex + 1]);
  };

  return (
    <PaperGradientBG variant="zen151515">
      <section className={`relative px-4 py-16 font-mono ${className}`}>
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {items.map((pillar) => {
              const Icon = pillar.icon;

              return (
                <div
                  key={pillar.id}
                  className={[
                    "rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-sm",
                    canOpenDetail(pillar.id)
                      ? "cursor-pointer transition-colors hover:border-[#AC8E66]/45"
                      : "",
                  ].join(" ")}
                  onClick={() => openDetail(pillar.id)}
                  onKeyDown={(e) => {
                    if (!canOpenDetail(pillar.id)) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDetail(pillar.id);
                    }
                  }}
                  role={canOpenDetail(pillar.id) ? "button" : undefined}
                  tabIndex={canOpenDetail(pillar.id) ? 0 : undefined}
                  aria-label={canOpenDetail(pillar.id) ? `${pillar.title} Details oeffnen` : undefined}
                >
                  <div className="flex items-center gap-1.5 px-3 py-2 
                  bg-[#1a1a1a] border-b border-[#AC8E66]/10">
                    <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                    <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                    <button
                      type="button"
                      className={[
                        "w-2 h-2 rounded-full",
                        canOpenDetail(pillar.id)
                          ? "bg-[#28c840] hover:brightness-125 cursor-pointer"
                          : "bg-[#28c840]",
                      ].join(" ")}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetail(pillar.id);
                      }}
                      aria-label={canOpenDetail(pillar.id) ? `${pillar.title} im Terminal oeffnen` : undefined}
                    />
                    <span className="ml-2 text-[10px] text-[#AC8E66] select-none">
                      {pillar.id}
                    </span>
                  </div>

                  <div className="px-4 py-4 bg-[#0F0F0F]">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 shrink-0 rounded-full border border-[#AC8E66]/30 flex items-center justify-center">
                        {Icon ? <Icon className="text-[#AC8E66] text-sm" /> : null}
                      </div>
                      <h3 className="text-sm font-semibold text-[#AC8E66]">
                        {pillar.title}
                      </h3>
                    </div>

                    <p className="text-xs text-[#EFEBDC]/70 mb-2">
                      <span className="text-[#AC8E66]">$</span> {pillar.cmd}
                    </p>

                    <div className="flex gap-1.5 text-xs text-[#EFEBDC] mb-1">
                      <span className="text-[#AC8E66]/80 shrink-0">&gt;</span>
                      <span>{pillar.desc}</span>
                    </div>

                    <div className="flex gap-1.5 text-xs text-green-400/80">
                      <span className="text-[#AC8E66]/80 shrink-0">&gt;</span>
                      <span>{pillar.hint}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <TerminalDetailOverlay
        isOpen={Boolean(activeDetail)}
        onClose={closeDetail}
        title={activePillar?.title || "Entwicklung"}
        subtitle={activeDetail?.subtitle || "ls services/"}
        lines={activeDetail?.lines || []}
        storageKey={activeDetail?.storageKey}
        ctaLabel={activeDetail?.ctaLabel}
        ctaPath={activeDetail?.ctaPath}
        ctaMode={activeDetail?.ctaMode}
        onPrev={handlePrev}
        onNext={handleNext}
        hasPrev={activeDetailIndex > 0}
        hasNext={activeDetailIndex >= 0 && activeDetailIndex < orderedDetailIds.length - 1}
      />
    </PaperGradientBG>
  );
}
